/* /employees/new and /employees/:id/edit — port of prototype pgEmpForm (5-step wizard).
 * Validation = domain validateEmployee (same as the API) + server 422 fields / 409 conflict. */
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAllEmployees, useCreateEmployee, useEmployee, useSettings, useToday, useUpdateEmployee, useUploadFile } from "../../data/hooks";
import { ApiError } from "../../data/provider";
import type { Employee, EmployeeInput, EmpDocument } from "../../data/types";
import { allowancesFor, validateEmployee } from "../../domain/compliance";
import { cityKeys, countryOf, ibanMessage, idMessage, type CountryInfo } from "../../domain/country";
import { addDays } from "../../domain/dates";
import { gosiOf } from "../../domain/payroll";
import { useI18n, LOOKUP } from "../../i18n";
import { hDate } from "../../lib/format";
import { useSession } from "../../app/session";
import { Affix, Alert, Button, Card, Chip, DualDate, EmptyState, ErrorBox, Field, Icon, Input, Kv, Money, PageHeader, Select, Skeleton, Toggle, errMsg, useToast } from "../../ui";
import IdScanModal, { type IdScanFields } from "./components/IdScanModal";
import { canWriteHr } from "./components/shared";
import { curL, useCountry } from "../../lib/country";

export interface FormState {
  nameEn: string; nameAr: string; nationality: string; gender: "M" | "F"; idNumber: string; dob: string; mobile: string; email: string; disabled: boolean;
  dept: string; title: string; branch: string; joined: string; contract: string; manager: string;
  basic: string; housing: string; transport: string; other: string; autoAllow: boolean; bank: string; iban: string;
  iqamaExpiry: string; passportNo: string; passportExpiry: string;
}

export const STEP_FIELDS: string[][] = [
  ["nameEn", "nameAr", "nationality", "gender", "idNumber", "dob", "mobile", "email"],
  ["dept", "title", "branch", "joined"],
  ["basic", "housing", "transport", "other", "bank", "iban"],
  ["iqamaExpiry", "passportNo", "passportExpiry", "documents"],
  [],
];
export const BANKS = ["Al Rajhi Bank", "SNB", "Riyad Bank", "Alinma Bank", "SAB", "Banque Saudi Fransi", "Arab National Bank", "Bank AlJazira", "Bank Albilad"];

const MSG_AR: Record<string, string> = {
  required: "مطلوب",
  "National ID must be 10 digits starting with 1": "رقم الهوية 10 أرقام يبدأ بـ 1",
  "Iqama number must be 10 digits starting with 2": "رقم الإقامة 10 أرقام يبدأ بـ 2",
  "IBAN must be SA followed by 22 digits": "الآيبان يبدأ بـ SA ويليه 22 رقمًا",
  "invalid email": "بريد إلكتروني غير صالح",
  "must be > 0": "يجب أن يكون أكبر من صفر",
  "must be ≥ 0": "يجب ألا يقل عن صفر",
  "valid date required": "أدخل تاريخًا صالحًا",
  "invalid date": "تاريخ غير صالح",
  "idNumber already used": "رقم الهوية مستخدم لموظف آخر",
};

const num = (s: string) => (s.trim() === "" ? NaN : Number(s.replace(/,/g, "")));

/** Banks offered for salaries: Saudi banks, or the country's own (contracts/countries.json). */
export const banksFor = (c: CountryInfo): string[] => (c.code === "SA" ? BANKS : c.banks.map((b) => b[0]));

function blank(today: string, c: CountryInfo): FormState {
  return {
    nameEn: "", nameAr: "", nationality: c.code, gender: "M", idNumber: "", dob: "", mobile: "", email: "", disabled: false,
    dept: "fin", title: "", branch: c.code === "SA" ? "jed" : c.cities[0][0], joined: today, contract: "Unlimited", manager: "106",
    basic: "", housing: "", transport: "", other: "0", autoAllow: true, bank: banksFor(c)[0], iban: "",
    iqamaExpiry: "", passportNo: "", passportExpiry: "",
  };
}
function fromEmployee(e: Employee, pct: { housing: number; transport: number }, c: CountryInfo): FormState {
  const d = allowancesFor(e.basic, pct);
  return {
    nameEn: e.nameEn, nameAr: e.nameAr, nationality: e.nationality, gender: e.gender, idNumber: e.idNumber, dob: e.dob, mobile: e.mobile.startsWith(c.phone) ? e.mobile.slice(c.phone.length).trim() : e.mobile, email: e.email, disabled: e.disabled,
    dept: e.dept, title: e.title, branch: e.branch, joined: e.joined, contract: e.nationality === c.code ? "Unlimited" : "Fixed term", manager: e.dept === "fin" ? "101" : "106",
    basic: String(e.basic), housing: String(e.housing), transport: String(e.transport), other: String(e.other), autoAllow: d.housing === e.housing && d.transport === e.transport,
    bank: e.bank, iban: e.iban.replace(/\s/g, ""), iqamaExpiry: e.iqamaExpiry ?? "", passportNo: "", passportExpiry: "",
  };
}

/** Form → API input (allowances resolved from settings % when auto). */
export function toInput(f: FormState, pct: { housing: number; transport: number }, isNew: boolean, c: CountryInfo = countryOf("SA")): Partial<EmployeeInput> {
  const basic = num(f.basic);
  const auto = allowancesFor(Number.isFinite(basic) ? basic : 0, pct);
  const out: Partial<EmployeeInput> = {
    nameEn: f.nameEn.trim(), nameAr: f.nameAr.trim(), nationality: f.nationality, gender: f.gender, idNumber: f.idNumber.trim(),
    dob: f.dob, mobile: f.mobile.trim() ? (f.mobile.trim().startsWith("+") ? f.mobile.trim() : c.phone + " " + f.mobile.trim()) : "", email: f.email.trim(), disabled: f.disabled,
    dept: f.dept, title: f.title.trim(), branch: f.branch, joined: f.joined,
    basic, housing: f.autoAllow ? auto.housing : num(f.housing), transport: f.autoAllow ? auto.transport : num(f.transport), other: f.other.trim() === "" ? 0 : num(f.other),
    bank: f.bank, iban: f.iban.replace(/\s/g, "").toUpperCase(),
    iqamaExpiry: f.nationality !== c.code && f.iqamaExpiry ? f.iqamaExpiry : null,
  };
  if (isNew) {
    const docs: EmpDocument[] = [];
    if (f.passportNo.trim() && f.passportExpiry) docs.push({ kind: "Passport", number: f.passportNo.trim(), expiry: f.passportExpiry });
    Object.assign(out, {
      status: "Probation", probationEnd: f.joined ? addDays(f.joined, 90) : null, gosiRegime: c.code === "SA" && f.nationality === "SA" ? "new" : "legacy",
      cchiClass: "Class B", ticket: null, documents: docs, dependants: [],
    } satisfies Partial<EmployeeInput>);
  }
  return out;
}

export function stepOfField(field: string): number {
  const i = STEP_FIELDS.findIndex((s) => s.includes(field));
  return i < 0 ? 0 : i;
}

export default function EmployeeForm() {
  const { id: idParam } = useParams();
  const editId = idParam ? Number(idParam) : null;
  const isEdit = editId != null;
  const q = useEmployee(editId ?? 0, { enabled: isEdit && Number.isInteger(editId) && (editId ?? 0) > 0 });
  const settingsQ = useSettings();
  const today = useToday();
  const { role, ready } = useSession();
  const { L } = useI18n();

  if (role && !canWriteHr(role)) return <Alert tone="warn" title={L("Only owners and HR can add or edit employees", "يمكن للمالك والموارد البشرية فقط إضافة الموظفين أو تعديلهم")} />;
  if (isEdit && (!Number.isInteger(editId) || (q.error instanceof ApiError && q.error.status === 404))) {
    return <EmptyState title={L("Employee not found", "الموظف غير موجود")} desc={L("The employee may have been removed, or the link is wrong.", "ربما حُذف الموظف أو أن الرابط غير صحيح.")} />;
  }
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (settingsQ.error) return <ErrorBox error={settingsQ.error} onRetry={() => settingsQ.refetch()} />;
  if (!ready || !settingsQ.data || (isEdit && !q.data)) return <Skeleton />;
  return <Wizard key={isEdit ? "e" + editId : "new"} employee={q.data ?? null} pct={settingsQ.data.allowances} today={today} />;
}

function Wizard({ employee, pct, today }: { employee: Employee | null; pct: { housing: number; transport: number }; today: string }) {
  const i = useI18n();
  const { t, L, isAr } = i;
  const nav = useNavigate();
  const toast = useToast();
  const settingsQ = useSettings();
  const allQ = useAllEmployees();
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  const upload = useUploadFile();
  const isEdit = !!employee;
  const country = useCountry();
  const cc = country.code;
  const [f, setF] = useState<FormState>(() => (employee ? fromEmployee(employee, pct, country) : blank(today, country)));
  const [step, setStep] = useState(0);
  const [shown, setShown] = useState<Set<string>>(new Set());
  const [server, setServer] = useState<Record<string, string>>({});
  const [scan, setScan] = useState(false);
  const [uploaded, setUploaded] = useState<Record<string, string>>({});
  const upRef = useRef<HTMLInputElement>(null);
  const upKind = useRef<string>("");

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    if (server[k as string]) setServer((s) => { const n = { ...s }; delete n[k as string]; return n; });
  };

  const input = useMemo(() => toInput(f, pct, !isEdit, country), [f, pct, isEdit, country]);
  const domainErrs = useMemo(() => validateEmployee(input, cc), [input, cc]);
  const errs: Record<string, string> = { ...domainErrs, ...server };
  const msg = (k: string) => {
    if (!shown.has(k) && !server[k]) return undefined;
    const m = errs[k];
    if (!m) return undefined;
    if (k === "nameEn" && m === "required") return L("Enter the employee's name", "أدخل اسم الموظف");
    if (isAr && cc !== "SA" && m === idMessage(f.nationality, cc)) return `${country.nationalId.resident[1]} يجب أن يكون ${country.nationalId.hint[1]}`;
    if (isAr && cc !== "SA" && m === ibanMessage(cc)) return `الآيبان يجب أن يبدأ بـ ${cc} ويتكون من ${country.iban.length} خانة`;
    return isAr ? MSG_AR[m] ?? m : m;
  };
  const stepErrs = (s: number) => STEP_FIELDS[s].filter((k) => errs[k]);
  const reveal = (s: number) => setShown((x) => new Set([...x, ...STEP_FIELDS[s]]));

  const goTo = (target: number) => {
    if (target > step) {
      for (let s = step; s < target; s++) {
        if (stepErrs(s).length) { reveal(s); setStep(s); return; }
      }
    }
    setStep(target);
  };

  const basic = num(f.basic) || 0;
  const auto = allowancesFor(basic, pct);
  const ho = f.autoAllow ? auto.housing : num(f.housing) || 0;
  const tr = f.autoAllow ? auto.transport : num(f.transport) || 0;
  const oth = num(f.other) || 0;
  const gs = settingsQ.data?.gosi;
  const fake = { ...(employee ?? ({} as Employee)), id: 0, nationality: f.nationality, gosiRegime: (f.nationality === "SA" ? (employee?.gosiRegime ?? "new") : "legacy") as Employee["gosiRegime"], basic, housing: ho, transport: tr, other: oth } as Employee;
  const g = gs ? gosiOf(fake, gs, today, cc) : null;
  const managers = (allQ.data ?? []).filter((e) => [106, 101, 111].includes(e.id));

  const save = async () => {
    for (let s = 0; s < 4; s++) if (stepErrs(s).length) { reveal(s); setStep(s); toast(L("Fix the highlighted fields first", "صحّح الحقول المظللة أولًا"), "bad"); return; }
    try {
      if (employee) {
        const e = await update.mutateAsync({ id: employee.id, patch: input });
        toast(L("Changes saved for ", "تم حفظ التعديلات لـ ") + i.name(e));
        nav("/employees/" + e.id);
      } else {
        const e = await create.mutateAsync(input as EmployeeInput);
        toast(L(e.nameEn + " added · contract sent to Qiwa", "تمت إضافة " + e.nameAr + " · أُرسل العقد إلى قوى"));
        nav("/employees/" + e.id);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServer({ idNumber: "idNumber already used" }); setShown((x) => new Set([...x, "idNumber"])); setStep(0);
        toast(L("Another employee already uses this ID / Iqama number", "رقم الهوية / الإقامة مستخدم لموظف آخر"), "bad");
      } else if (err instanceof ApiError && err.fields && Object.keys(err.fields).length) {
        setServer(err.fields); setShown((x) => new Set([...x, ...Object.keys(err.fields ?? {})]));
        setStep(Math.min(...Object.keys(err.fields).map(stepOfField)));
        toast(errMsg(err), "bad");
      } else toast(errMsg(err), "bad");
    }
  };

  const applyScan = (s: IdScanFields) => {
    setF((x) => ({
      ...x,
      nameEn: s.nameEn ?? x.nameEn, nameAr: s.nameAr ?? x.nameAr, idNumber: s.idNumber ?? x.idNumber,
      nationality: s.nationality && LOOKUP.NAT[s.nationality] ? s.nationality : x.nationality, dob: s.dob ?? x.dob,
      iqamaExpiry: s.expiry && (s.nationality ?? x.nationality) !== "SA" ? s.expiry : x.iqamaExpiry,
    }));
    setServer({});
    toast(L("Employee details filled from the Iqama — review and continue", "تم تعبئة البيانات من الإقامة — راجعها وتابع"));
  };

  const fld = (k: keyof FormState, label: ReactNode, node: (id: string, bad: boolean) => ReactNode, o: { req?: boolean; hint?: ReactNode; cls?: string } = {}) => {
    const id = "f-" + k;
    const m = msg(k);
    return <Field label={label} req={o.req} err={m} hint={o.hint} htmlFor={id} className={o.cls}>{node(id, !!m)}</Field>;
  };
  const txt = (k: keyof FormState, extra: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {}) => (id: string, bad: boolean) => (
    <Input id={id} value={String(f[k])} bad={bad} onChange={(e) => set(k, e.target.value as never)} onBlur={() => setShown((x) => new Set([...x, k]))} data-testid={id} {...extra} />
  );
  const sel = (k: keyof FormState, options: (string | [string, string])[]) => (id: string) => (
    <Select id={id} value={String(f[k])} onChange={(e) => set(k, e.target.value as never)} options={options} data-testid={id} />
  );

  const steps = [t("Personal"), t("Job & contract"), t("Salary & allowances"), t("Documents"), L("Review", "المراجعة")];
  /** A citizen of the company's country (Saudi in Saudi Arabia). */
  const isSa = f.nationality === cc;
  const DOCS: [string, string, boolean][] = [
    ["id", isSa ? t("National ID") : t("Iqama"), true], ["passport", t("Passport"), !isSa],
    ["offer", L("Signed offer letter", "خطاب العرض الموقع"), false], ["certs", L("Academic certificates (attested)", "الشهادات العلمية (مصدقة)"), false], ["iban", L("IBAN certificate", "شهادة الآيبان"), false],
  ];

  const bodies: ReactNode[] = [
    <>
      <div className="aibar" data-testid="idscan-bar">
        <Icon name="sparkle" />
        <div><b>{L("Save time: scan the Iqama or ID", "وفّر الوقت: امسح الإقامة أو الهوية")}</b><span className="xs muted">{L("AI fills names in both languages, ID number, nationality and dates.", "يعبئ الذكاء الأسماء باللغتين ورقم الهوية والجنسية والتواريخ.")}</span></div>
        <Button className="aibtn" icon="sparkle" onClick={() => setScan(true)} data-testid="btn-idscan">{L("Fill with AI", "تعبئة بالذكاء الاصطناعي")}</Button>
      </div>
      <div className="form2">
        {fld("nameEn", L("Full name (English)", "الاسم الكامل (إنجليزي)"), txt("nameEn"), { req: true })}
        {fld("nameAr", L("Full name (Arabic)", "الاسم الكامل (عربي)"), txt("nameAr", { dir: "rtl" }), { req: true })}
        {fld("nationality", t("Nationality"), sel("nationality", Object.keys(LOOKUP.NAT).map((k) => [k, i.nat(k)] as [string, string])), { req: true })}
        {fld("gender", t("Gender"), sel("gender", [["M", t("Male")], ["F", t("Female")]]))}
        {cc === "SA"
          ? fld("idNumber", isSa ? t("National ID") : L("Iqama number", "رقم الإقامة"), txt("idNumber", { inputMode: "numeric", maxLength: 10 }), { req: true, hint: L("10 digits", "10 أرقام") })
          : fld("idNumber", L(...(isSa ? country.nationalId.citizen : country.nationalId.resident)), txt("idNumber", { inputMode: "numeric", maxLength: 15 }), { req: true, hint: L(...country.nationalId.hint) })}
        {fld("dob", t("Date of birth"), txt("dob", { type: "date" }), { hint: f.dob ? hDate(f.dob) : undefined })}
        {fld("mobile", t("Mobile"), (id, bad) => <Affix pre={<span dir="ltr">{country.phone}</span>}><Input id={id} value={f.mobile} bad={bad} inputMode="tel" onChange={(e) => set("mobile", e.target.value)} data-testid={id} /></Affix>)}
        {fld("email", t("Email"), txt("email", { type: "email" }))}
        <div className="field"><div className="row nw" style={{ gap: 10 }}><Toggle checked={f.disabled} onChange={(v) => set("disabled", v)} label={L("Registered person with disability", "شخص ذو إعاقة مسجل")} testId="f-disabled" /><span className="sm">{L("Registered person with disability (counts ×4 in Nitaqat)", "شخص ذو إعاقة مسجل (يُحتسب ×4 في نطاقات)")}</span></div></div>
      </div>
    </>,
    <div className="form2">
      {fld("dept", t("Department"), sel("dept", Object.keys(LOOKUP.DEPT).map((k) => [k, i.dept(k)] as [string, string])), { req: true })}
      {fld("title", t("Job title"), txt("title"), { req: true })}
      {fld("branch", t("Branch"), sel("branch", Array.from(new Set([...cityKeys(cc, Object.keys(LOOKUP.BR)), f.branch])).map((k) => [k, i.branch(k)] as [string, string])), { req: true })}
      {fld("joined", L("Start date", "تاريخ المباشرة"), txt("joined", { type: "date" }), { req: true, hint: f.joined ? hDate(f.joined) : undefined })}
      {fld("contract", t("Contract type"), sel("contract", [["Unlimited", t("Unlimited")], ["Fixed term", t("Fixed term")]]), { hint: L("Sent to Qiwa for authentication on save", "يُرسل إلى قوى للتوثيق عند الحفظ") })}
      {fld("manager", t("Reports to"), sel("manager", managers.length ? managers.map((e) => [String(e.id), i.name(e)] as [string, string]) : [["106", "—"]]))}
    </div>,
    <div className="g g-2-1">
      <div className="form2">
        {fld("basic", t("Basic salary"), (id, bad) => <Affix pre={curL(L)}><Input id={id} value={f.basic} bad={bad} inputMode="decimal" onChange={(e) => set("basic", e.target.value)} onBlur={() => setShown((x) => new Set([...x, "basic"]))} data-testid={id} /></Affix>, { req: true, hint: isSa && basic > 0 && basic < 4000 ? <span style={{ color: "var(--warning)" }}>{L("Saudis under SAR 4,000 count as half in Nitaqat", "السعودي براتب أقل من 4,000 ريال يُحتسب نصفًا في نطاقات")}</span> : undefined })}
        <div className="field span-all"><div className="row nw" style={{ gap: 10 }}><Toggle checked={f.autoAllow} onChange={(v) => { set("autoAllow", v); if (!v) setF((x) => ({ ...x, autoAllow: false, housing: String(auto.housing), transport: String(auto.transport) })); }} label={L("Use company allowance %", "استخدام نسب البدلات المعتمدة")} testId="f-autoAllow" /><span className="sm">{L("Housing " + pct.housing + "% and transport " + pct.transport + "% of basic (Settings)", "السكن " + pct.housing + "% والنقل " + pct.transport + "% من الأساسي (الإعدادات)")}</span></div></div>
        {fld("housing", t("Housing") + " (" + pct.housing + "%)", (id, bad) => <Affix pre={curL(L)}><Input id={id} value={f.autoAllow ? String(auto.housing) : f.housing} readOnly={f.autoAllow} bad={bad} inputMode="decimal" onChange={(e) => set("housing", e.target.value)} data-testid={id} /></Affix>)}
        {fld("transport", t("Transport") + " (" + pct.transport + "%)", (id, bad) => <Affix pre={curL(L)}><Input id={id} value={f.autoAllow ? String(auto.transport) : f.transport} readOnly={f.autoAllow} bad={bad} inputMode="decimal" onChange={(e) => set("transport", e.target.value)} data-testid={id} /></Affix>)}
        {fld("other", t("Other"), (id, bad) => <Affix pre={curL(L)}><Input id={id} value={f.other} bad={bad} inputMode="decimal" onChange={(e) => set("other", e.target.value)} data-testid={id} /></Affix>)}
        {fld("bank", t("Bank"), sel("bank", banksFor(country).includes(f.bank) ? banksFor(country) : [f.bank, ...banksFor(country)]))}
        {fld("iban", t("IBAN"), txt("iban", { dir: "ltr" }), { req: true, hint: cc === "SA" ? L("SA + 22 digits", "SA + 22 رقمًا") : L(`${cc} IBAN, ${country.iban.length} characters · e.g. ${country.iban.example}`, `آيبان ${cc} من ${country.iban.length} خانة · مثل ${country.iban.example}`), cls: "span-all" })}
      </div>
      <Card title={L("Cost preview", "معاينة التكلفة")} testId="cost-preview">
        <Kv items={[
          [t("Gross"), <Money value={basic + ho + tr + oth} />],
          [t("Employee share"), <Money value={-(g?.emp ?? 0)} />],
          [t("Net pay"), <b><Money value={basic + ho + tr + oth - (g?.emp ?? 0)} /></b>],
          [t("Employer share"), <Money value={g?.er ?? 0} />],
          [L("Nitaqat effect", "الأثر على نطاقات"), isSa ? <Chip tone="ok" nodot>+{cc !== "SA" ? 1 : f.disabled ? 4 : basic > 0 && basic < 4000 ? 0.5 : 1} {t("Saudi")}</Chip> : <Chip nodot>{t("Non-Saudi")}</Chip>],
        ]} />
      </Card>
    </div>,
    <div className="stack">
      {!isSa && (
        <div className="form2">
          {fld("iqamaExpiry", L("Iqama expiry", "انتهاء الإقامة"), txt("iqamaExpiry", { type: "date" }), { hint: f.iqamaExpiry ? <DualDate date={f.iqamaExpiry} inline /> : L("Adds the Iqama to the document tracker", "تُضاف الإقامة إلى متابعة الوثائق") })}
        </div>
      )}
      {!isEdit && (
        <div className="form2">
          {fld("passportNo", L("Passport number", "رقم الجواز"), txt("passportNo", { dir: "ltr" }))}
          {fld("passportExpiry", L("Passport expiry", "انتهاء الجواز"), txt("passportExpiry", { type: "date" }))}
        </div>
      )}
      {DOCS.map(([k, label, req]) => (
        <div key={k} className="list-row" data-testid={"doc-req-" + k}>
          <span className={"kpi-ico " + (uploaded[k] ? "" : "warn")}><Icon name={uploaded[k] ? "check" : "upload"} /></span>
          <span className="grow">{label}{req && <> <span className="req">*</span></>}{uploaded[k] && <div className="xs muted">{uploaded[k]}</div>}</span>
          {uploaded[k] ? <Chip status="Submitted" /> : <Button variant="sm" icon="upload" loading={upload.isPending && upKind.current === k} onClick={() => { upKind.current = k; upRef.current?.click(); }} data-testid={"btn-upload-" + k}>{t("Upload")}</Button>}
        </div>
      ))}
      <input ref={upRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" aria-label={t("Upload")} data-testid="doc-upload-input"
        onChange={async (ev) => {
          const file = ev.target.files?.[0]; ev.target.value = "";
          if (!file) return;
          const k = upKind.current;
          try { await upload.mutateAsync({ file, name: file.name, folder: "documents" }); setUploaded((u) => ({ ...u, [k]: file.name })); toast(L("Uploaded", "تم الرفع")); }
          catch (e) { toast(errMsg(e), "bad"); }
        }} />
      <p className="xs muted">PDF, JPG · {L("up to 10 MB", "حتى 10 ميغابايت")}</p>
    </div>,
    <div className="g g-2" data-testid="review">
      <Card title={t("Personal")}><Kv items={[[L("Name", "الاسم"), <>{f.nameEn || "—"}<div className="muted sm" dir="rtl">{f.nameAr}</div></>], [t("Nationality"), i.nat(f.nationality)], [isSa ? t("National ID") : t("Iqama"), <><span className="num">{f.idNumber || "—"}</span> {errs.idNumber && <Chip status="Rejected" tone="bad" />}</>], [t("Mobile"), f.mobile ? <span className="num" dir="ltr">+966 {f.mobile}</span> : "—"]]} /></Card>
      <Card title={L("Job & pay", "الوظيفة والراتب")}><Kv items={[[t("Job title"), (f.title || "—") + " · " + i.dept(f.dept)], [t("Branch"), i.branch(f.branch)], [L("Start date", "تاريخ المباشرة"), f.joined ? <DualDate date={f.joined} /> : "—"], [t("Gross"), <Money value={basic + ho + tr + oth} />], [t("IBAN"), <><span className="num" dir="ltr">{f.iban || "—"}</span> {errs.iban && <Chip status="Rejected" tone="bad" />}</>]]} /></Card>
      <div className="span-all">
        {Object.keys(errs).length > 0
          ? <Alert tone="bad" tint title={L("Some details need fixing", "بعض البيانات تحتاج تصحيحًا")} desc={Object.keys(errs).map((k) => k + ": " + (isAr ? MSG_AR[errs[k]] ?? errs[k] : errs[k])).join(" · ")} testId="review-errors" />
          : <Alert tone="info" tint title={L("When you save", "عند الحفظ")} desc={isEdit ? L("We update the employee file. Salary changes are flagged for Qiwa and GOSI wage updates.", "سنحدّث ملف الموظف، وتُرصد تعديلات الراتب لتحديث قوى والتأمينات.") : L("We create the employee file, send the contract to Qiwa, register with GOSI and start onboarding.", "سننشئ ملف الموظف ونرسل العقد إلى قوى ونسجله في التأمينات ونبدأ إجراءات المباشرة.")} />}
      </div>
    </div>,
  ];

  const busy = create.isPending || update.isPending;
  const back = isEdit ? { to: "/employees/" + employee!.id, label: i.name(employee!) } : { to: "/employees", label: t("Employees") };

  return (
    <div data-testid="employee-form">
      <PageHeader back={back} title={isEdit ? t("Edit") + " · " + i.name(employee!) : t("Add employee")} sub={L("Step " + (step + 1) + " of 5", "الخطوة " + (step + 1) + " من 5") + " · " + steps[step]} />
      <ol className="wz" data-testid="wizard-steps">
        {steps.map((s, k) => (
          <li key={k} className={k < step ? "done" : k === step ? "cur" : ""}>
            <button type="button" onClick={() => goTo(k)} aria-current={k === step ? "step" : undefined} data-testid={"step-" + k}>
              <span className="wb" /><span className="wl">{k < step && <Icon name="check" className="s" />}{s}{k < 4 && stepErrs(k).length > 0 && shown.has(STEP_FIELDS[k][0]) ? " •" : ""}</span>
            </button>
          </li>
        ))}
      </ol>
      <section className="card card-b" data-testid={"step-body-" + step}>{bodies[step]}</section>
      <div className="actionbar">
        <Button variant="ghost" onClick={() => nav(back.to)} data-testid="btn-cancel">{t("Cancel")}</Button>
        <span className="spacer" />
        {step > 0 && <Button icon="chevron-left" onClick={() => setStep(step - 1)} data-testid="btn-back">{t("Back")}</Button>}
        {step < 4
          ? <Button variant="pri" end="chevron-right" onClick={() => goTo(step + 1)} data-testid="btn-next">{t("Next")}</Button>
          : <Button variant="pri" icon="check" loading={busy} onClick={save} data-testid="btn-save">{isEdit ? t("Save") : L("Save employee", "حفظ الموظف")}</Button>}
      </div>
      {scan && <IdScanModal onClose={() => setScan(false)} onApply={applyScan} />}
    </div>
  );
}
